import React, { useEffect, useState } from "react";
import { Typography, TextField, IconButton } from "@mui/material";
import UserService from "../services/user.service";
import BookingService from "../services/booking.service";
import useApi from "../hooks/useApi";
import MenuHeader from "./MenuHeader";
import FlyProItem from "./FlyProItem";
// import ZonesMap from './ZonesMap';
import CustomBarCharts from "./CustomBarCharts";
import CustomHeatChart from "./CustomHeatChart";
import CustomDonutChart from "./CustomDonutChart";
import CreateTaskModal from "./CreateBookingModal";
import { useTranslatableData } from "../hooks/useTranslatable";
import { useTheme } from "@mui/material/styles";
import CalendarManagementService from "../services/calendar_management.service";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Union from "../assets/images/Union.png";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

function Statistics() {
  const { t } = useTranslation(['dashboard']);
  const api = useApi();
  const theme = useTheme();
  const [flypros, setFlypros] = useState([]);
  const [zones, setZones] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [mostRequested, setMostRequested] = useState(null);
  const [leastRequested, setLeastRequested] = useState(null);
  const [requested, setRequested] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [newClients, setNewClients] = useState(null);
  const [topTreatments, setTopTreatments] = useState([]);
  const [flopTreatments, setFlopTreatments] = useState([]);
  const [ageCategories, setAgeCategories] = useState([]);
  const [devicesCategories, setDevicesCategories] = useState([]);

  // Use translated data for display
  const translatedTopTreatments = useTranslatableData(topTreatments);
  const translatedFlopTreatments = useTranslatableData(flopTreatments);

  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  const formatDate = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(formatDate(oneMonthAgo));
  const [endDate, setEndDate] = useState(formatDate(today));

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [viewAge, setViewAge] = useState("age");
  const [viewSoins, setViewSoins] = useState("top");
  const [chartData, setChartData] = useState([]);
  const [chartDataDonut, setChartDataDonut] = useState([]);

  const chartDataSoins =
    viewSoins === "top" ? translatedTopTreatments : translatedFlopTreatments;
  const optionsSoins = [
    { value: "top", label: "Top 5 treatment types" },
    { value: "bad", label: "Flop 5 treatment types" },
  ];

  const chartDataAge = viewAge === "age" ? ageCategories : devicesCategories;
  const optionsAge = [
    { value: "age", label: "Age and demographics " },
    { value: "device", label: "Breakdown by platfrom" },
  ];

  const filtered = flypros.filter((item) =>
    item.extra.full_name.toLowerCase().includes(search.toLowerCase())
  );

  const fetchStats = async (start, end) => {
    setLoading(true);
    try {
      const filters = { with: "media,roles", sort: "firstname" };
      const [flyprosRes, bookingStats, treatmentStats, calendarData] =
        await Promise.all([
          UserService.indexByRole(filters, "ROLE_FLYPRO", api),
          BookingService.heatmap({ start_date: start, end_date: end }, api),
          BookingService.treatmentStats(
            { start_date: start, end_date: end },
            api
          ),
          CalendarManagementService.calendarStats(
            { start_date: start, end_date: end },
            api
          ),
        ]);
      console.log(
        "API Response:",
        flyprosRes,
        bookingStats,
        treatmentStats,
        calendarData
      );

      setFlypros(flyprosRes || []);

      setHeatmapData(bookingStats.heatmap || []);
      setMostRequested(bookingStats.most_requested_slot || null);
      setLeastRequested(bookingStats.least_requested_slot || null);
      setRequested(bookingStats.requested || null);
      setActiveUsers(bookingStats.active_users || null);
      setNewClients(bookingStats.new_clients ?? 0);

      setTopTreatments(treatmentStats?.top_treatments || []);
      setFlopTreatments(treatmentStats?.flop_treatments || []);
      setAgeCategories(treatmentStats?.age_categories || []);
      setDevicesCategories(treatmentStats?.devices_categories || []);
      setChartData(treatmentStats?.bookings_by_hour || []);
      setZones(treatmentStats?.zone_bookings || []);
      setBookings(treatmentStats?.city_bookings || []);
      const combinedDonutData = [
        ...(treatmentStats?.cancelled_stats || []),
        ...(calendarData || []),
      ];
      setChartDataDonut(combinedDonutData);
    } catch (error) {
      console.error("Error loading statistics:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStats(startDate, endDate);
  }, []);

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    if (name === "start_date") {
      setStartDate(value);
      fetchStats(value, endDate);
    } else if (name === "end_date") {
      setEndDate(value);
      fetchStats(startDate, value);
    }
  };

  console.log({ endDate, startDate });

  return (
    <>
      <MenuHeader />

      <div className="bloc-search background-breadcrumb bloc-search-mobile  bloc-dashboard">
        <div className="container">
          <form className="form-stat">
            <div className="text-search input-350-width">
              <input
                type="date"
                name="start_date"
                value={startDate}
                onChange={handleDateChange}
                className="form-text"
              />
              <input
                type="date"
                name="end_date"
                value={endDate}
                onChange={handleDateChange}
                className="form-text"
              />
            </div>
          </form>
          <CreateTaskModal api={api} />
        </div>
      </div>

      <div className="two-column">
        <div className="left">
          <div className="stats-grid">
            <div className="stat-card">
              Most requested times/days
              <br />
              <div>{mostRequested?.count}</div>
            </div>
            <div className="stat-card">
              Least requested hours/days
              <br />
              <div>{leastRequested?.count}</div>
            </div>
            <div className="stat-card">
              Requested
              <br />
              <div>{requested}</div>
            </div>
            <div className="stat-card">
              Active Users
              <br />
              <div>{activeUsers}</div>
            </div>
            <div className="stat-card">
              {t('statistics.newClients')}
              <br />
              <div>{newClients ?? 0}</div>
            </div>
          </div>

          <div className="orders-chart">
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                ml: 3,
                textAlign: "left",
                color: "var(--black-100, #1C1C1C)",
                fontFamily: "Marcellus, serif",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "20px",
              }}
            >
              Les commandes
            </Typography>

            <div className="chart-placeholder">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <Tooltip />

                  <Legend
                    verticalAlign="top"
                    align="right"
                    layout="horizontal"
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{
                      fontSize: "12px",
                      fontFamily: "Marcellus, sans-serif",
                    }}
                  />

                  <XAxis
                    dataKey="hour"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                  />

                  {/* Canceled */}
                  <Line
                    type="monotone"
                    dataKey="Annulé"
                    stroke="#FF0000"
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="4 4"
                  />

                  {/* Completed */}
                  <Line
                    type="monotone"
                    dataKey="Terminé"
                    stroke="#45C343"
                    strokeWidth={2}
                    dot={false}
                  />

                  {/* Assigned */}
                  <Line
                    type="monotone"
                    dataKey="Assigned"
                    stroke="#FF8A00"
                    strokeWidth={2}
                    dot={false}
                  />

                  {/* Not Assigned */}
                  <Line
                    type="monotone"
                    dataKey="Not Assigned"
                    stroke="#B3BEC5"
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="4 4"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bottom-grid">
            <div className="sideRight_donut">
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  ml: 3,
                  textAlign: "left",
                  color: "var(--black-100, #1C1C1C)",
                  fontFamily: "Marcellus, serif",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                Booked time
              </Typography>
              <CustomDonutChart data={chartDataDonut} />
            </div>
            <div className="demographics">
              <CustomBarCharts
                chartData={chartDataAge}
                view={viewAge}
                setView={setViewAge}
                options={optionsAge}
              />
            </div>
          </div>
          <div className="demograph">
            <CustomBarCharts
              chartData={chartDataSoins}
              view={viewSoins}
              setView={setViewSoins}
              options={optionsSoins}
            />
          </div>
          <div className="demograph" style={{ padding: "16px 40px 4px 4px" }}>
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                ml: 3,
                textAlign: "left",
                color: "var(--black-100, #1C1C1C)",
                fontFamily: "Marcellus, serif",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "20px",
              }}
            >
              Most requested times
            </Typography>

            <CustomHeatChart data={heatmapData} />
          </div>
        </div>

        <div className="right">
          <div className="sideRight_list">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px",
                justifyContent: "space-between",
              }}
            >
              <h2>flyPRO</h2>
              {!showSearch && (
                <IconButton onClick={() => setShowSearch((prev) => !prev)}>
                  <img
                    src={Union}
                    alt="Union Icon"
                    style={{ width: 20, height: 16 }}
                  />
                </IconButton>
              )}

              {showSearch && (
                <>
                  <TextField
                    margin="dense"
                    label="Search by name"
                    type="text"
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ ml: 1, flexGrow: 1 }}
                  />
                  <IconButton onClick={() => setShowSearch((prev) => !prev)}>
                    <CloseIcon />
                  </IconButton>
                </>
              )}
            </div>

            {filtered.length > 0 ? (
              filtered.map((item, index) => (
                <FlyProItem
                  key={index}
                  item={item}
                  startDate={startDate}
                  endDate={endDate}
                />
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: "center" }}
              >
                No flyPRO found
              </Typography>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Statistics;
